/**
 * EMR Redux Slice
 * Manages state for EMRv2 (patients, documents, UI state)
 */

import { createSlice } from '@reduxjs/toolkit';
import {
  fetchPatients,
  fetchPatientProfile,
  updatePatientProfile,
  updatePatientLastOpened,
  fetchPatientDocuments,
  fetchPendingApprovals,
  fetchGeneralPendingDocuments,
  fetchPatientTreatment,
  fetchEMRData,
  saveEMRData,
  fetchDiagnoses,
  addDiagnosis,
  deleteDiagnosis,
  fetchICDCodes,
  addICDCode,
  addTxNote,
  updateTxNote,
  updateGeneralNote,
  deleteTxNote,
  fetchEncounters,
  fetchSoapNote,
  saveSoapNote,
  fetchShortcuts,
  fetchSoapTemplates,
} from '../thunks';

const initialState = {
  // Pagination
  currentPage: 1,
  pageSize: 15,

  // Search & Filters
  searchTerm: '',
  status: 'Active',
  formStatusFilters: ['no_link', 'link_generated', 'submitted', 'approved'],
  showDeceased: false,
  minAge: null,
  maxAge: null,

  // UI State
  selectedPatientId: null,
  showPatientForm: false,
  openedPatients: [],
  activeTab: null,
  showPatientsList: true,

  // Patients List
  patients: [],
  patientsTotal: 0,
  patientsLoading: false,
  patientsLoadingMore: false,
  patientsError: null,

  // Patient Profiles (normalized by ID)
  patientProfiles: {},
  profilesLoading: {},
  profilesError: {},

  // Documents by patient ID
  documentsByPatient: {},
  documentsLoading: {},
  documentsError: {},

  // Pending Approvals
  pendingApprovals: [],
  pendingApprovalsCount: 0,
  pendingApprovalsLoading: false,
  pendingApprovalsError: null,

  // General Pending Documents
  generalPendingDocuments: [],
  generalPendingCount: 0,
  generalPendingLoading: false,
  generalPendingError: null,

  // Treatments by patient ID
  treatmentsByPatient: {},
  treatmentsLoading: {},
  treatmentsError: {},

  // Modals
  allInOnePopupOpen: false,
  activeTreatment: {},
  allInOnePopupSelection: null,

  // EMR Form Data (Summary section — normalized by patient ID)
  emrDataByPatient: {},
  emrDataLoading: {},
  emrDataSaving: {},
  emrDataError: {},

  // Global diagnoses list (shared across all patients)
  diagnoses: [],
  diagnosesLoading: false,
  diagnosesError: null,
  diagnosisAdding: false,
  diagnosisDeleting: {},   // { [id]: boolean }

  // Global ICD-10 codes list (shared across all patients)
  icdCodes: [],
  icdCodesLoading: false,
  icdCodesLoaded: false,  // true once a load succeeded, even if the list was empty
  icdCodesError: null,
  icdCodeAdding: false,

  // TX Notes loading states (note data lives on openedPatients[n])
  txNoteAdding: {},        // { [patientId]: boolean }
  txNoteDeleting: {},      // { [noteId]: boolean }
  txNoteUpdating: {},      // { [noteId]: boolean }
  generalNoteUpdating: {}, // { [patientId]: boolean }

  // Encounters (paginated, keyed by patientId)
  // Shape: { [patientId]: { data: [], total: 0, totalPages: 0, loading: false, error: null } }
  encountersByPatient: {},

  // Active section tab ('summary' | 'documents' | 'profile' | 'soap-{key}' | ...)
  activeSectionTab: 'summary',

  // SOAP Note Tabs — keyed by patientId
  // Shape: { [patientId]: [{ key, soapNoteId, date, label }] }
  // - key:        unique tab id (timestamp string)
  // - soapNoteId: null until SOAP note is created & saved, then its MongoDB _id
  // - date:       YYYY-MM-DD
  // - label:      MM-DD-YYYY (display in tab)
  soapTabsByPatient: {},

  // SOAP Note Data — keyed by tabKey (not patientId, since one patient can have multiple open tabs)
  // Shape: { [tabKey]: { soapNoteId, patientId, date, customName, appointmentType, isComped,
  //                      subjective, objective, assessment, careplan, planNote, medicationsNote,
  //                      treatmentId } }
  soapNotesByKey: {},
  soapNotesLoading: {},  // { [tabKey]: boolean }
  soapNotesSaving: {},   // { [tabKey]: boolean }
  soapNotesError: {},    // { [tabKey]: string|null }

  // Shared shortcut templates (single-dot menu) — fetched once, shared across all notes
  shortcuts: [],
  shortcutsLoading: false,

  // Section templates by type (double-dot menu) — { [type]: [...] }
  soapTemplates: {},
  soapTemplatesLoading: {},
};

const emrSlice = createSlice({
  name: 'emr',
  initialState,
  reducers: {
    // Pagination
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },

    // Search & Filters
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
      state.currentPage = 1;
    },
    setFormStatusFilters: (state, action) => {
      state.formStatusFilters = action.payload;
      state.currentPage = 1;
    },
    setShowDeceased: (state, action) => {
      state.showDeceased = action.payload;
      state.currentPage = 1;
    },
    setAgeRange: (state, action) => {
      state.minAge = action.payload.minAge;
      state.maxAge = action.payload.maxAge;
      state.currentPage = 1;
    },
    clearAgeFilter: (state) => {
      state.minAge = null;
      state.maxAge = null;
      state.currentPage = 1;
    },
    resetEMRFilters: (state) => {
      state.searchTerm = '';
      state.status = 'Active';
      state.formStatusFilters = ['no_link', 'link_generated', 'submitted', 'approved'];
      state.showDeceased = false;
      state.minAge = null;
      state.maxAge = null;
      state.currentPage = 1;
    },

    // Replace a single encounter in the list by _id — avoids full table refetch
    patchEncounter: (state, action) => {
      const { patientId, encounter } = action.payload;
      const bucket = state.encountersByPatient[patientId];
      if (!bucket) return;
      const idx = bucket.data.findIndex((e) => e._id === encounter._id);
      if (idx !== -1) bucket.data[idx] = encounter;
    },

    // Remove an encounter by _id (e.g. when a SOAP note gets linked to an order)
    removeEncounter: (state, action) => {
      const { patientId, encounterId } = action.payload;
      const bucket = state.encountersByPatient[patientId];
      if (!bucket) return;
      bucket.data = bucket.data.filter((e) => e._id !== encounterId);
      if (bucket.total > 0) bucket.total -= 1;
    },

    // Patient Selection
    setSelectedPatientId: (state, action) => {
      state.selectedPatientId = action.payload;
    },

    // Patient Form
    setShowPatientForm: (state, action) => {
      state.showPatientForm = action.payload;
    },

    // Opened Patients & Tabs
    addOpenedPatient: (state, action) => {
      const patient = action.payload;
      state.openedPatients = state.openedPatients.filter((p) => p._id !== patient._id);
      state.openedPatients.unshift(patient);
      // Reset section to Summary when the active patient is actually changing
      // (e.g. opening EMR from TodayView with a URL patientId while a different
      // patient's section was restored from localStorage). Use addOpenedPatientWithSection
      // when a non-Summary landing section is desired.
      if (state.activeTab !== patient._id) {
        state.activeTab = patient._id;
        state.activeSectionTab = 'summary';
      }
      state.showPatientsList = false;
    },
    // Open a patient and pin a specific initial section. Used when opening
    // from Manage Documents → Approvals where we want to land on Documents.
    // Section-reset on tab switch happens in setActiveTab, not here, so the
    // pinned section survives.
    addOpenedPatientWithSection: (state, action) => {
      const { patient, section } = action.payload;
      state.openedPatients = state.openedPatients.filter((p) => p._id !== patient._id);
      state.openedPatients.unshift(patient);
      state.activeTab = patient._id;
      state.showPatientsList = false;
      state.activeSectionTab = section;
    },
    removeOpenedPatient: (state, action) => {
      const patientId = action.payload;
      state.openedPatients = state.openedPatients.filter((p) => p._id !== patientId);
      // The encounters list is kept only while the patient is open
      delete state.encountersByPatient[patientId];

      if (state.openedPatients.length === 0) {
        state.activeTab = null;
        state.showPatientsList = true;
        state.activeSectionTab = 'summary';
      } else if (state.activeTab === patientId) {
        // The patient on screen was closed: the last opened one is shown, from its
        // Summary (the closed patient's section, a SOAP note say, isn't the next one's)
        state.activeTab = state.openedPatients[state.openedPatients.length - 1]._id;
        state.activeSectionTab = 'summary';
      }
      // A patient closed in the background leaves the one on screen as it is
    },
    setActiveTab: (state, action) => {
      const next = action.payload;
      // Reset section to Summary when switching between opened patient tabs.
      // Skip when value didn't actually change (avoids stomping on a section
      // explicitly set elsewhere).
      if (state.activeTab !== next) {
        state.activeTab = next;
        state.activeSectionTab = 'summary';
      }
    },
    setActiveSectionTab: (state, action) => {
      state.activeSectionTab = action.payload;
    },
    setShowPatientsList: (state, action) => {
      state.showPatientsList = action.payload;
    },
    setOpenedPatients: (state, action) => {
      state.openedPatients = action.payload;
    },
    setSoapTabsByPatient: (state, action) => {
      state.soapTabsByPatient = action.payload;
    },

    // Documents Management
    setPatientDocuments: (state, action) => {
      const { patientId, files, folderName, folderId } = action.payload;
      state.documentsByPatient[patientId] = { files, folderName, folderId };
    },
    updatePatientFiles: (state, action) => {
      const { patientId, files } = action.payload;
      if (state.documentsByPatient[patientId]) {
        state.documentsByPatient[patientId].files = files;
      }
    },
    clearPatientDocuments: (state, action) => {
      const patientId = action.payload;
      delete state.documentsByPatient[patientId];
      delete state.documentsLoading[patientId];
      delete state.documentsError[patientId];
    },

    // Update patient in opened patients list
    updateOpenedPatient: (state, action) => {
      const updatedPatient = action.payload;
      state.openedPatients = state.openedPatients.map((p) =>
        p._id === updatedPatient._id ? updatedPatient : p
      );
      state.patients = state.patients.map((p) =>
        p._id === updatedPatient._id ? updatedPatient : p
      );
      if (updatedPatient?._id) {
        state.patientProfiles[updatedPatient._id] = updatedPatient;
      }
      if (updatedPatient?.['Customer ID']) {
        state.patientProfiles[updatedPatient['Customer ID']] = updatedPatient;
      }
    },

    // Modals
    setAllInOnePopupOpen: (state, action) => {
      state.allInOnePopupOpen = action.payload;
    },
    setActiveTreatment: (state, action) => {
      state.activeTreatment = action.payload;
    },
    setAllInOnePopupSelection: (state, action) => {
      state.allInOnePopupSelection = action.payload;
    },

    // ── SOAP Note Tabs ─────────────────────────────────────────────────────
    // Add a new SOAP note tab for a patient.
    // If a tab with the same soapNoteId already exists, do nothing (caller
    // should have checked first and switched to the existing tab instead).
    addSoapTab: (state, action) => {
      const { patientId, key, soapNoteId, date, label, treatmentId } = action.payload;
      if (!state.soapTabsByPatient[patientId]) {
        state.soapTabsByPatient[patientId] = [];
      }
      // Guard: never add a duplicate soapNoteId (the real-ID case)
      if (soapNoteId) {
        const already = state.soapTabsByPatient[patientId].some(
          (t) => t.soapNoteId === soapNoteId
        );
        if (already) return;
      }
      state.soapTabsByPatient[patientId].push({ key, soapNoteId, date, label });
      // Pre-seed noteData with treatmentId so linkedTreatment loads immediately
      if (treatmentId && !soapNoteId) {
        if (!state.soapNotesByKey[key]) state.soapNotesByKey[key] = {};
        state.soapNotesByKey[key].treatmentId = treatmentId;
      }
    },

    // A tab kept when its patient is closed gets a new key: answers still on their way
    // for the closed tab (kept under the old key) can't reach it once it is opened again.
    rekeySoapTab: (state, action) => {
      const { patientId, key, newKey } = action.payload;
      const tab = state.soapTabsByPatient[patientId]?.find((t) => t.key === key);
      if (tab) tab.key = newKey;
    },

    // Remove a SOAP note tab (close button)
    removeSoapTab: (state, action) => {
      const { patientId, key } = action.payload;
      if (state.soapTabsByPatient[patientId]) {
        state.soapTabsByPatient[patientId] = state.soapTabsByPatient[
          patientId
        ].filter((t) => t.key !== key);
      }
    },

    // Once SOAPNotesSection creates/fetches the note it reports back its _id.
    // We store it so that future "open from encounter list" clicks hit the
    // existing tab instead of opening a second one for the same note.
    updateSoapTabNoteId: (state, action) => {
      const { patientId, key, soapNoteId } = action.payload;
      if (state.soapTabsByPatient[patientId]) {
        const tab = state.soapTabsByPatient[patientId].find((t) => t.key === key);
        if (tab) tab.soapNoteId = soapNoteId;
      }
    },

    // Also update the display label (e.g. when the user changes the date
    // inside the SOAP note itself)
    updateSoapTabLabel: (state, action) => {
      const { patientId, key, label } = action.payload;
      if (state.soapTabsByPatient[patientId]) {
        const tab = state.soapTabsByPatient[patientId].find((t) => t.key === key);
        if (tab) tab.label = label;
      }
    },

    // ── SOAP Note Data (keyed by tabKey) ───────────────────────────────────
    // Patch any fields in the soap note for a given tabKey (local optimistic update)
    patchSoapNoteData: (state, action) => {
      const { tabKey, ...fields } = action.payload;
      if (!state.soapNotesByKey[tabKey]) {
        state.soapNotesByKey[tabKey] = {};
      }
      Object.assign(state.soapNotesByKey[tabKey], fields);
    },

    // Remove SOAP note data when tab is closed (clean up memory)
    clearSoapNoteData: (state, action) => {
      const tabKey = action.payload;
      delete state.soapNotesByKey[tabKey];
      delete state.soapNotesLoading[tabKey];
      delete state.soapNotesSaving[tabKey];
      delete state.soapNotesError[tabKey];
    },

    // Reset all state
    resetEMRState: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      // Fetch patients
      .addCase(fetchPatients.pending, (state, action) => {
        if (action.meta.arg?.append) {
          state.patientsLoadingMore = true;
        } else {
          state.patientsLoading = true;
        }
        state.patientsError = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.patientsLoading = false;
        state.patientsLoadingMore = false;
        const incomingPatients = action.payload.patients || [];
        if (action.meta.arg?.append) {
          const existingKeys = new Set(
            state.patients.map((patient) => patient._id || patient['Customer ID'])
          );
          const newPatients = incomingPatients.filter((patient) => {
            const key = patient._id || patient['Customer ID'];
            return !key || !existingKeys.has(key);
          });
          state.patients = [...state.patients, ...newPatients];
        } else {
          state.patients = incomingPatients;
        }
        state.patientsTotal = action.payload.total;
        state.currentPage = Number(action.meta.arg?.page) || state.currentPage;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.patientsLoading = false;
        state.patientsLoadingMore = false;
        state.patientsError = action.payload;
      })

      // Fetch patient profile
      .addCase(fetchPatientProfile.pending, (state, action) => {
        const customerId = action.meta.arg;
        state.profilesLoading[customerId] = true;
        state.profilesError[customerId] = null;
      })
      .addCase(fetchPatientProfile.fulfilled, (state, action) => {
        const { customerId, patient } = action.payload;
        state.patientProfiles[customerId] = patient;
        state.profilesLoading[customerId] = false;
        // Enrich opened patient entry with full profile data if already open.
        // Match by _id (MongoDB ObjectId) since patient._id is set on the full profile response.
        // customerId is 'Customer ID' string — do NOT compare with _id.
        if (patient?._id) {
          state.openedPatients = state.openedPatients.map((p) =>
            p._id === patient._id ? patient : p
          );
        }
      })
      .addCase(fetchPatientProfile.rejected, (state, action) => {
        const customerId = action.meta.arg;
        state.profilesLoading[customerId] = false;
        state.profilesError[customerId] = action.payload;
      })

      // Update patient profile
      .addCase(updatePatientProfile.pending, (state, action) => {
        const { patientId } = action.meta.arg;
        state.profilesLoading[patientId] = true;
      })
      .addCase(updatePatientProfile.fulfilled, (state, action) => {
        const { patientId, patient } = action.payload;
        state.patientProfiles[patientId] = patient;
        state.profilesLoading[patientId] = false;

        if (patient && patient['Customer ID']) {
          state.patientProfiles[patient['Customer ID']] = patient;
        }

        // Update in patients list
        state.patients = state.patients.map((p) => (p._id === patientId ? patient : p));

        // Update in opened patients
        state.openedPatients = state.openedPatients.map((p) =>
          p._id === patientId ? patient : p
        );
      })
      .addCase(updatePatientProfile.rejected, (state, action) => {
        const { patientId } = action.meta.arg;
        state.profilesLoading[patientId] = false;
        state.profilesError[patientId] = action.payload;
      })

      // Fetch patient documents
      .addCase(fetchPatientDocuments.pending, (state, action) => {
        const patientId = action.meta.arg;
        state.documentsLoading[patientId] = true;
        state.documentsError[patientId] = null;
      })
      .addCase(fetchPatientDocuments.fulfilled, (state, action) => {
        const { patientId, files } = action.payload;
        state.documentsByPatient[patientId] = { files };
        state.documentsLoading[patientId] = false;
      })
      .addCase(fetchPatientDocuments.rejected, (state, action) => {
        const patientId = action.meta.arg;
        state.documentsLoading[patientId] = false;
        state.documentsError[patientId] = action.payload;
      })

      // Fetch pending approvals
      .addCase(fetchPendingApprovals.pending, (state) => {
        state.pendingApprovalsLoading = true;
        state.pendingApprovalsError = null;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.pendingApprovalsLoading = false;
        state.pendingApprovals = action.payload.patients;
        state.pendingApprovalsCount = action.payload.totalCount;
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.pendingApprovalsLoading = false;
        state.pendingApprovalsError = action.payload;
      })

      // Fetch general pending documents
      .addCase(fetchGeneralPendingDocuments.pending, (state) => {
        state.generalPendingLoading = true;
        state.generalPendingError = null;
      })
      .addCase(fetchGeneralPendingDocuments.fulfilled, (state, action) => {
        state.generalPendingLoading = false;
        state.generalPendingDocuments = action.payload.documents;
        state.generalPendingCount = action.payload.totalCount;
      })
      .addCase(fetchGeneralPendingDocuments.rejected, (state, action) => {
        state.generalPendingLoading = false;
        state.generalPendingError = action.payload;
      })

      // Fetch EMR data (summary section form)
      .addCase(fetchEMRData.pending, (state, action) => {
        const patientId = action.meta.arg;
        state.emrDataLoading[patientId] = true;
        state.emrDataError[patientId] = null;
      })
      .addCase(fetchEMRData.fulfilled, (state, action) => {
        const { patientId, data } = action.payload;
        state.emrDataByPatient[patientId] = data;
        state.emrDataLoading[patientId] = false;
      })
      .addCase(fetchEMRData.rejected, (state, action) => {
        const patientId = action.meta.arg;
        state.emrDataLoading[patientId] = false;
        state.emrDataError[patientId] = action.payload;
      })

      // Save EMR data
      .addCase(saveEMRData.pending, (state, action) => {
        const { patientId } = action.meta.arg;
        state.emrDataSaving[patientId] = true;
      })
      .addCase(saveEMRData.fulfilled, (state, action) => {
        const { patientId, data } = action.payload;
        state.emrDataSaving[patientId] = false;
        // Merge saved fields into the existing EMR data
        state.emrDataByPatient[patientId] = {
          ...state.emrDataByPatient[patientId],
          ...data,
        };
      })
      .addCase(saveEMRData.rejected, (state, action) => {
        const { patientId } = action.meta.arg;
        state.emrDataSaving[patientId] = false;
        state.emrDataError[patientId] = action.payload;
      })

      // Fetch patient treatment
      .addCase(fetchPatientTreatment.pending, (state, action) => {
        const { customerId } = action.meta.arg;
        state.treatmentsLoading[customerId] = true;
        state.treatmentsError[customerId] = null;
      })
      .addCase(fetchPatientTreatment.fulfilled, (state, action) => {
        const { customerId, treatments, totalCount, latestTreatment } = action.payload;
        state.treatmentsByPatient[customerId] = {
          treatments,
          totalCount,
          latestTreatment,
        };
        state.treatmentsLoading[customerId] = false;
      })
      .addCase(fetchPatientTreatment.rejected, (state, action) => {
        const { customerId } = action.meta.arg;
        state.treatmentsLoading[customerId] = false;
        state.treatmentsError[customerId] = action.payload;
      })

      // Fetch global diagnoses list
      .addCase(fetchDiagnoses.pending, (state) => {
        state.diagnosesLoading = true;
        state.diagnosesError = null;
      })
      .addCase(fetchDiagnoses.fulfilled, (state, action) => {
        state.diagnoses = action.payload;
        state.diagnosesLoading = false;
      })
      .addCase(fetchDiagnoses.rejected, (state, action) => {
        state.diagnosesLoading = false;
        state.diagnosesError = action.payload;
      })

      // Add diagnosis
      .addCase(addDiagnosis.pending, (state) => {
        state.diagnosisAdding = true;
      })
      .addCase(addDiagnosis.fulfilled, (state, action) => {
        state.diagnosisAdding = false;
        state.diagnoses.push(action.payload);
      })
      .addCase(addDiagnosis.rejected, (state) => {
        state.diagnosisAdding = false;
      })

      // Delete diagnosis
      .addCase(deleteDiagnosis.pending, (state, action) => {
        state.diagnosisDeleting[action.meta.arg] = true;
      })
      .addCase(deleteDiagnosis.fulfilled, (state, action) => {
        const id = action.payload;
        state.diagnoses = state.diagnoses.filter((d) => d._id !== id);
        delete state.diagnosisDeleting[id];
      })
      .addCase(deleteDiagnosis.rejected, (state, action) => {
        delete state.diagnosisDeleting[action.meta.arg];
      })

      // ── ICD-10 Codes ──────────────────────────────────────────────────────

      // Fetch ICD codes
      .addCase(fetchICDCodes.pending, (state) => {
        state.icdCodesLoading = true;
        state.icdCodesError = null;
      })
      .addCase(fetchICDCodes.fulfilled, (state, action) => {
        state.icdCodes = action.payload;
        state.icdCodesLoading = false;
        state.icdCodesLoaded = true;
      })
      .addCase(fetchICDCodes.rejected, (state, action) => {
        state.icdCodesLoading = false;
        state.icdCodesError = action.payload;
      })

      // Add ICD code
      .addCase(addICDCode.pending, (state) => {
        state.icdCodeAdding = true;
      })
      .addCase(addICDCode.fulfilled, (state, action) => {
        state.icdCodeAdding = false;
        state.icdCodes.push(action.payload);
      })
      .addCase(addICDCode.rejected, (state) => {
        state.icdCodeAdding = false;
      })

      // ── TX Notes ──────────────────────────────────────────────────────────

      // Add TX note
      .addCase(addTxNote.pending, (state, action) => {
        state.txNoteAdding[action.meta.arg.patientId] = true;
      })
      .addCase(addTxNote.fulfilled, (state, action) => {
        const { patientId, patient } = action.payload;
        delete state.txNoteAdding[patientId];
        if (patient?._id) {
          state.openedPatients = state.openedPatients.map((p) =>
            p._id === patient._id ? patient : p
          );
        }
      })
      .addCase(addTxNote.rejected, (state, action) => {
        delete state.txNoteAdding[action.meta.arg.patientId];
      })

      // Update TX note
      .addCase(updateTxNote.pending, (state, action) => {
        state.txNoteUpdating[action.meta.arg.noteId] = true;
      })
      .addCase(updateTxNote.fulfilled, (state, action) => {
        const { patientId, noteId, patient } = action.payload;
        delete state.txNoteUpdating[noteId];
        if (patient?._id) {
          state.openedPatients = state.openedPatients.map((p) =>
            p._id === patient._id ? patient : p
          );
        }
      })
      .addCase(updateTxNote.rejected, (state, action) => {
        delete state.txNoteUpdating[action.meta.arg.noteId];
      })

      // Update general note
      .addCase(updateGeneralNote.pending, (state, action) => {
        state.generalNoteUpdating[action.meta.arg.patientId] = true;
      })
      .addCase(updateGeneralNote.fulfilled, (state, action) => {
        const { patientId, patient } = action.payload;
        delete state.generalNoteUpdating[patientId];
        if (patient?._id) {
          state.openedPatients = state.openedPatients.map((p) =>
            p._id === patient._id ? patient : p
          );
        }
      })
      .addCase(updateGeneralNote.rejected, (state, action) => {
        delete state.generalNoteUpdating[action.meta.arg.patientId];
      })

      // Delete TX note
      .addCase(deleteTxNote.pending, (state, action) => {
        state.txNoteDeleting[action.meta.arg.noteId] = true;
      })
      .addCase(deleteTxNote.fulfilled, (state, action) => {
        const { noteId, patient } = action.payload;
        delete state.txNoteDeleting[noteId];
        if (patient?._id) {
          state.openedPatients = state.openedPatients.map((p) =>
            p._id === patient._id ? patient : p
          );
        }
      })
      .addCase(deleteTxNote.rejected, (state, action) => {
        delete state.txNoteDeleting[action.meta.arg.noteId];
      })

      // ── Encounters ───────────────────────────────────────────────────────────
      .addCase(fetchEncounters.pending, (state, action) => {
        const { patientId, append } = action.meta.arg;
        if (!state.encountersByPatient[patientId]) {
          state.encountersByPatient[patientId] = { data: [], total: 0, totalPages: 0, loading: false, error: null };
        }
        state.encountersByPatient[patientId].loading = true;
        state.encountersByPatient[patientId].error = null;
        // The list being loaded now: answers for an earlier one are dropped (see fulfilled)
        if (!append) state.encountersByPatient[patientId].listRequestId = action.meta.requestId;
      })
      .addCase(fetchEncounters.fulfilled, (state, action) => {
        const { patientId, data, total, totalPages, append, version, referencedSoapNotes, requestedAt, listRequestId } = action.payload;
        const { page, type, customerId } = action.meta.arg;
        const bucket = state.encountersByPatient[patientId];
        // Answer for a list that is no longer the current one (reloaded meanwhile, e.g.
        // under another type filter, or the patient was closed): a later page would be
        // appended to the wrong list, an earlier first page would replace the newer one
        if (listRequestId && (!bucket || (bucket.listRequestId && bucket.listRequestId !== listRequestId))) return;
        // No page after this one: it came back empty, or it was the last page. Stops
        // infinite scroll even if the rows never add up to the total.
        const endReached = !(data && data.length > 0) || (Number.isFinite(totalPages) && page >= totalPages);
        if (append && bucket) {
          // Infinite scroll — append, deduplicating by _id
          const existingIds = new Set(bucket.data.map((e) => e._id));
          const newItems = (data || []).filter((e) => !existingIds.has(e._id));
          bucket.data = [...bucket.data, ...newItems];
          bucket.total = total || bucket.total;
          bucket.totalPages = totalPages || bucket.totalPages;
          bucket.loading = false;
          bucket.error = null;
          // What EncountersSection needs to carry on from here after a remount
          bucket.page = page;
          bucket.endReached = endReached;
          if (Array.isArray(referencedSoapNotes)) {
            const prev = bucket.referencedSoapNotes || [];
            bucket.referencedSoapNotes = [...prev, ...referencedSoapNotes.filter((n) => !prev.some((p) => p._id === n._id))];
          }
        } else {
          // Replace mode (filter change, patient change, refresh)
          state.encountersByPatient[patientId] = {
            data: data || [],
            total: total || 0,
            totalPages: totalPages || 0,
            loading: false,
            error: null,
            // Lets EncountersSection reuse this list when it mounts again (see its
            // patient effect): the page reached, the filter, the server's fingerprint
            // of the list, and the linked SOAP notes.
            page,
            endReached,
            type,
            customerId,
            version: version ?? null,
            // When page 1 was asked for; a list older than EncountersSection's maximum age is reloaded
            loadedAt: requestedAt ?? null,
            referencedSoapNotes: Array.isArray(referencedSoapNotes) ? referencedSoapNotes : [],
            listRequestId: bucket?.listRequestId,
          };
        }
      })
      .addCase(fetchEncounters.rejected, (state, action) => {
        const { patientId, append } = action.meta.arg;
        const bucket = state.encountersByPatient[patientId];
        // An earlier list's failure doesn't mark the current one failed
        if (!append && bucket?.listRequestId && action.meta.requestId && bucket.listRequestId !== action.meta.requestId) return;
        if (state.encountersByPatient[patientId]) {
          state.encountersByPatient[patientId].loading = false;
          state.encountersByPatient[patientId].error = action.payload || 'Failed to load encounters';
        }
      })

      // ── SOAP Note fetch ───────────────────────────────────────────────────
      .addCase(fetchSoapNote.pending, (state, action) => {
        const { tabKey } = action.meta.arg;
        state.soapNotesLoading[tabKey] = true;
        state.soapNotesError[tabKey] = null;
      })
      .addCase(fetchSoapNote.fulfilled, (state, action) => {
        const { tabKey, data } = action.payload;
        state.soapNotesLoading[tabKey] = false;
        const note = data?.soapNote || data;
        // DB stores plan note as plan.note (nested). EMRv2 reads it as planNote (flat).
        // Map on fetch so editors receive the correct value.
        if (note && note.plan?.note !== undefined && !note.planNote) {
          state.soapNotesByKey[tabKey] = { ...note, planNote: note.plan.note };
        } else {
          state.soapNotesByKey[tabKey] = note;
        }
      })
      .addCase(fetchSoapNote.rejected, (state, action) => {
        const { tabKey } = action.meta.arg;
        state.soapNotesLoading[tabKey] = false;
        state.soapNotesError[tabKey] = action.payload || 'Failed to load SOAP note';
      })

      // ── SOAP Note save ────────────────────────────────────────────────────
      .addCase(saveSoapNote.pending, (state, action) => {
        const { tabKey } = action.meta.arg;
        state.soapNotesSaving[tabKey] = true;
      })
      .addCase(saveSoapNote.fulfilled, (state, action) => {
        const { tabKey, data, _savedField, isCreation } = action.payload;
        // The tab was closed meanwhile (clearSoapNoteData): its late answer isn't kept
        if (!(tabKey in state.soapNotesSaving)) return;
        state.soapNotesSaving[tabKey] = false;
        const note = data?.soapNote || data;
        if (note) {
          const current = state.soapNotesByKey[tabKey] || {};
          if (_savedField && !isCreation) {
            // Partial merge — only system metadata. The saved field already holds the
            // text that was sent (optimistic update), or newer text typed since; the
            // SOAP note tab (useSoapNoteData) takes the rest of the answer in itself.
            state.soapNotesByKey[tabKey] = {
              ...current,
              ...(note._id !== undefined && { _id: note._id }),
              ...(note.updatedAt !== undefined && { updatedAt: note.updatedAt }),
            };
          } else if (_savedField) {
            // The note was just created from what this tab had. Text typed in it while
            // the creation was on its way is newer than the copy that comes back: the
            // answer only fills fields the tab doesn't have yet, plus _id and updatedAt.
            state.soapNotesByKey[tabKey] = {
              ...note,
              ...current,
              ...(note._id !== undefined && { _id: note._id }),
              ...(note.updatedAt !== undefined && { updatedAt: note.updatedAt }),
            };
          } else {
            // Full merge for saves without a specific field tag
            state.soapNotesByKey[tabKey] = { ...current, ...note };
          }
        }
      })
      .addCase(saveSoapNote.rejected, (state, action) => {
        const { tabKey } = action.meta.arg;
        if (!(tabKey in state.soapNotesSaving)) return;
        state.soapNotesSaving[tabKey] = false;
      })

      // ── Shortcuts (single-dot menu) ───────────────────────────────────────
      .addCase(fetchShortcuts.pending, (state) => {
        state.shortcutsLoading = true;
      })
      .addCase(fetchShortcuts.fulfilled, (state, action) => {
        state.shortcutsLoading = false;
        state.shortcuts = action.payload || [];
      })
      .addCase(fetchShortcuts.rejected, (state) => {
        state.shortcutsLoading = false;
      })

      // ── Section templates (double-dot menu) ───────────────────────────────
      .addCase(fetchSoapTemplates.pending, (state, action) => {
        const type = action.meta.arg;
        state.soapTemplatesLoading[type] = true;
      })
      .addCase(fetchSoapTemplates.fulfilled, (state, action) => {
        const { type, data } = action.payload;
        state.soapTemplatesLoading[type] = false;
        state.soapTemplates[type] = data || [];
      })
      .addCase(fetchSoapTemplates.rejected, (state, action) => {
        const type = action.meta.arg;
        state.soapTemplatesLoading[type] = false;
      });
  },
});

export const {
  setCurrentPage,
  setPageSize,
  setSearchTerm,
  setStatus,
  setFormStatusFilters,
  setShowDeceased,
  setAgeRange,
  clearAgeFilter,
  resetEMRFilters,
  setSelectedPatientId,
  setShowPatientForm,
  addOpenedPatient,
  addOpenedPatientWithSection,
  removeOpenedPatient,
  setActiveTab,
  setActiveSectionTab,
  setShowPatientsList,
  setOpenedPatients,
  setSoapTabsByPatient,
  setAllInOnePopupOpen,
  setActiveTreatment,
  setAllInOnePopupSelection,
  setPatientDocuments,
  updatePatientFiles,
  clearPatientDocuments,
  updateOpenedPatient,
  addSoapTab,
  removeSoapTab,
  rekeySoapTab,
  updateSoapTabNoteId,
  updateSoapTabLabel,
  patchSoapNoteData,
  clearSoapNoteData,
  resetEMRState,
  patchEncounter,
  removeEncounter,
} = emrSlice.actions;

export default emrSlice.reducer;
